import { ref } from 'vue';
import { storeToRefs } from 'pinia';
import myAccountStore, { MyAccountStore } from './my-account-store';
import { getMyOrganizations } from './api';

const isLoading = ref(false);
let runningRequest: Promise<void> | null = null;

const useMyOrganization = (loadImmediately = false) => {
  const store = myAccountStore();
  const { organizations } = storeToRefs(store) as unknown as MyAccountStore;
  const { reset, setOrganization, invalidateOrganizations } = store;
  const isFetchingOrganizations = ref<boolean>(false);

  const shouldRefreshData = (): boolean => {
    const ttl = 2 * 60 * 60 * 1000; // 2 hours
    return Date.now() - organizations.value.updatedAt > ttl;
  };

  /**
   *
   * Load all user organizations and save to the persistent store if the data doesnt't exist or older than constraints (2 hours).
   * "isLoading" ref is for tracing the loading.
   * "organizations" where the data will be held.
   */
  const loadMyOrganizations = async ({ force = false }: { force?: boolean } = {}): Promise<void> => {
    if (runningRequest) {
      return runningRequest;
    }

    if (!force && !shouldRefreshData()) {
      return Promise.resolve();
    }

    isLoading.value = true;
    runningRequest = (async () => {
      try {
        const chunkSize = 100;
        const { data, meta } = await getMyOrganizations(chunkSize, 0);

        if (data?.length === 0 || meta?.totalCount === 0) {
          setOrganization({ data: [], meta: { totalCount: 0 }, updatedAt: Date.now() });
          return;
        }

        const leftOrganizations = meta.totalCount - (data.length + 1);

        if (leftOrganizations > 0) {
          const count = Math.ceil(leftOrganizations / chunkSize);
          const calls = Array.from({ length: count }).map((_, i) => getMyOrganizations(100, (i + 1) * chunkSize));
          const responses = await Promise.all(calls);
          data.push(...responses.flatMap((r) => r.data));
        }

        setOrganization({ data, meta, updatedAt: Date.now() });
      } finally {
        isLoading.value = false;
        runningRequest = null;
      }
    })();

    return runningRequest;
  };

  if (loadImmediately && !isLoading.value) {
    loadMyOrganizations();
  }

  /**
   * direct access to API.
   * "isFetchingOrganizations" ref is used for tracking fetching
   * No error handling done.
   */
  const fetchMyOrganizations = (limit: number, offset: number) => {
    isFetchingOrganizations.value = true;
    return getMyOrganizations(limit, offset).finally(() => {
      isFetchingOrganizations.value = false;
    });
  };

  return {
    fetchMyOrganizations,
    isLoading,
    loadMyOrganizations,
    organizations,
    invalidateOrganizations,
    reset,
  };
};

export default useMyOrganization;
